// @ts-strict-ignore
import List from 'antd/lib/list';
import * as React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import styles from '../styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import { ControlOutlined, NotificationOutlined, SortAscendingOutlined, ThunderboltFilled } from '@ant-design/icons';
import { runwayColors } from '../styles/globalStyles';
import { Automation, AutomationType } from '../../types';
import { setSeenNewAutomations, setSeenUpdatedAutomations } from '../../helpers/localStorageHelper';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import Search from 'antd/lib/input/Search';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { BucketAutomationSettings } from '../automations/BucketAutomationSettings';
import { AppAutomationSettings } from '../automations/AppAutomationSettings';
import { automationIsEnabled, AutomationRow } from '../automations/AutomationRow';
import { selectSortedFilteredAutomations } from '@/selectors/memoizedSelectors';
import { selectBuildDistributionBucketById } from '@/selectors/selectors';
import { groupBy } from 'lodash';
import { SortTableButton } from '@/components/tableFilters/SortTableButton';
import { SettingsSortType } from '@/types/sorts';
import { AutomationSettingsContext } from '@/helpers/automationHelpers';
import { useAutomationSettingFields } from '@/utils/hooks/useAutomationSettingFields';

interface AutomationsComponentProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
    bucketId?: string;
    hideHeader?: boolean;
}

export const AutomationsComponent: React.FC<AutomationsComponentProps> = (props: AutomationsComponentProps) => {
    // Fetch the fields with the page, so the settings modal opens without waiting
    useAutomationSettingFields(props.orgId, props.appId, !!props.bucketId);
    const { bucketId } = props;

    const location = useLocation();
    const history = useHistory();
    const params = qs.parse(location?.search);

    const automations = useSelector((state: RootState) => {
        if (props.bucketId) {
            return selectBuildDistributionBucketById(state, bucketId).automations;
        } else {
            return selectSortedFilteredAutomations(state, props.appId);
        }
    });

    const [showAutomationSettings, setShowAutomationSettings] = React.useState(false);
    const [automationIdSelectedForSettings, setAutomationIdSelectedForSettings] = React.useState(null);
    const [searchValue, setSearchValue] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [selectedSortType, setSelectedSortType] = React.useState<SettingsSortType>(SettingsSortType.Name);

    const processedAutomations = React.useMemo(() => {
        const filtered = automations.filter((automation) => {
            const search = searchValue.toLowerCase();
            return (
                automation.name.toLowerCase().includes(search) ||
                (automation.keywords ?? []).some((keyword) => keyword.toLowerCase().includes(search))
            );
        });

        return [...filtered].sort((a, b) => {
            switch (selectedSortType) {
                case SettingsSortType.Name:
                    return a.name.localeCompare(b.name);

                case SettingsSortType.EnabledFirst:
                    if (automationIsEnabled(a) !== automationIsEnabled(b)) {
                        return automationIsEnabled(a) ? -1 : 1;
                    }
                    return a.name.localeCompare(b.name);

                case SettingsSortType.NewUpdatedFirst: {
                    const isNewOrUpdated = (automation: Automation) => automation.isNew || automation.isUpdated;
                    if (isNewOrUpdated(a) !== isNewOrUpdated(b)) {
                        return isNewOrUpdated(a) ? -1 : 1;
                    }
                    return a.name.localeCompare(b.name);
                }
                default:
                    return 0;
            }
        });
    }, [searchValue, automations, selectedSortType]);

    const groupedAutomationsByType = React.useMemo(() => {
        return groupBy(processedAutomations, 'type') as Record<AutomationType, Automation[]>;
    }, [processedAutomations]);

    React.useEffect(() => {
        // save seen new/updated automations to local storage
        if (automations) {
            const seenNewAutomations: Partial<Record<AutomationType, true>> = {};
            const seenUpdatedAutomations: Partial<Record<AutomationType, true>> = {};
            automations.forEach((automation) => {
                if (automation.isNew) {
                    seenNewAutomations[automation.type] = true;
                }
                if (automation.isUpdated) {
                    seenUpdatedAutomations[automation.type] = true;
                }
            });
            setSeenNewAutomations(seenNewAutomations);
            setSeenUpdatedAutomations(seenUpdatedAutomations);
        }
    }, []);

    React.useEffect(() => {
        const automationId = params?.id;

        if (automationId) {
            setShowAutomationSettings(true);
            setAutomationIdSelectedForSettings(automationId);

            history.replace({ search: null });
        }
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }} ref={props.refProp}>
            {!props.hideHeader && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Space size={8}>
                        <Text style={styles.appSettingsSectionTitleStyle}>Available automations</Text>
                        <ThunderboltFilled style={{ color: runwayColors.statusGrey }} />
                    </Space>
                    <Space>
                        <Search
                            placeholder="Search automations"
                            allowClear
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onSearch={(value) => {
                                setSearchValue(value);
                            }}
                            style={{ width: 400 }}
                        />
                        <SortTableButton<SettingsSortType>
                            selectedSortType={selectedSortType}
                            sortOptions={[
                                {
                                    sortType: SettingsSortType.Name,
                                    text: 'Name',
                                    icon: <SortAscendingOutlined style={{ color: runwayColors.primary }} />,
                                },
                                {
                                    sortType: SettingsSortType.EnabledFirst,
                                    text: 'Enabled',
                                    icon: <ControlOutlined style={{ color: runwayColors.primary }} />,
                                },
                                {
                                    sortType: SettingsSortType.NewUpdatedFirst,
                                    text: 'Recently updated',
                                    icon: <NotificationOutlined style={{ color: runwayColors.primary }} />,
                                },
                            ]}
                            onSelectSort={(sortType) => {
                                setSelectedSortType(sortType);
                            }}
                        />
                    </Space>
                </div>
            )}
            <List
                header={null}
                pagination={{
                    hideOnSinglePage: true,
                    defaultPageSize: 10,
                    current: currentPage,
                    onChange: (page) => setCurrentPage(page),
                }}
                dataSource={Object.entries(groupedAutomationsByType)}
                renderItem={([automationTypeString, automations]) => {
                    const automationType = automationTypeString as AutomationType;

                    return (
                        <List.Item key={automationType}>
                            <PlanRestrictedFeatureWrapper automationType={automationType}>
                                {(isDisabledDueToPricingPlan) => (
                                    <AutomationRow
                                        appId={props.appId}
                                        automations={automations}
                                        isDisabledDueToPricingPlan={isDisabledDueToPricingPlan}
                                        onSelectAutomationForSettings={(automationId) => {
                                            setAutomationIdSelectedForSettings(automationId);
                                            setShowAutomationSettings(true);
                                        }}
                                    />
                                )}
                            </PlanRestrictedFeatureWrapper>
                        </List.Item>
                    );
                }}
            ></List>
            {automationIdSelectedForSettings &&
                (bucketId ?
                    <BucketAutomationSettings
                        appId={props.appId}
                        orgId={props.orgId}
                        bucketId={bucketId}
                        onClose={() => setShowAutomationSettings(false)}
                        open={showAutomationSettings}
                        automationType={automationIdSelectedForSettings}
                        onAfterClose={() => {
                            setAutomationIdSelectedForSettings(null);
                        }}
                    />
                :   <AppAutomationSettings
                        onAutomationSelected={(automationId) => setAutomationIdSelectedForSettings(automationId)}
                        appId={props.appId}
                        orgId={props.orgId}
                        onClose={() => setShowAutomationSettings(false)}
                        open={showAutomationSettings}
                        automationId={automationIdSelectedForSettings}
                        context={AutomationSettingsContext.App}
                        onAfterClose={() => {
                            setAutomationIdSelectedForSettings(null);
                        }}
                    />)}
        </div>
    );
};
